import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  PersonOutline as DetailsIcon,
  Shield as CoverageIcon,
  EditNote as NotesIcon,
  PersonRemove as RemoveCoverageIcon,
} from '@mui/icons-material';
import {
  COMPACT_CELL_PADDING,
  COMPACT_HEADER_HEIGHT,
  COMPACT_ROW_HEIGHT,
  getCompactAttendanceBadge,
  getCompactCoverageBadge,
  getCompactRowHighlight,
} from '../../utils/compactDashboardUtils';
import { formatCoverageModeLabel, canShowMarkCoverageNotRequired } from '../../utils/coverageDashboardUtils';
import {
  EmployeeDashboardGroup,
  formatAssignmentWindow,
  getEmployeeGroupExpanded,
  groupDashboardRowsByEmployee,
  toggleEmployeeGroupExpanded,
} from '../../utils/employeeDashboardGrouping';
import { CampaignSummaryItem } from '../../utils/operationsDashboardUtils';
import { OPS_COLORS } from '../../constants/operationsDashboardColors';

interface AttendanceGridCompactViewProps {
  rows: any[];
  campaignSummary: CampaignSummaryItem[];
  highlightedRowId: string | null;
  rowRefs: React.MutableRefObject<Record<string, HTMLTableRowElement | null>>;
  canManage: boolean;
  groupByCampaign: boolean;
  onToggleGroupByCampaign: (value: boolean) => void;
  onRowClick: (row: any) => void;
  onAssignCoverage: (row: any) => void;
  onRemoveCoverage?: (row: any) => void;
  onNotRequired: (row: any) => void;
  onAddNote: (row: any) => void;
  onViewTimeline: (row: any) => void;
}

const stickySx = (left: number, isHeader = false) => ({
  position: 'sticky' as const,
  left,
  zIndex: isHeader ? 4 : 2,
  bgcolor: isHeader ? '#f4f6f8' : 'background.paper',
  boxShadow: left > 0 ? '2px 0 4px -2px rgba(0,0,0,0.06)' : undefined,
});

const cellSx = {
  py: 0,
  px: 0.75,
  fontSize: '0.68rem',
  lineHeight: 1.15,
  height: COMPACT_ROW_HEIGHT,
  maxHeight: COMPACT_ROW_HEIGHT,
  padding: COMPACT_CELL_PADDING,
  whiteSpace: 'nowrap' as const,
};

const StatusChip: React.FC<{ label: string; bgcolor: string; color: string }> = ({ label, bgcolor, color }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      height: 18,
      fontSize: '0.58rem',
      fontWeight: 700,
      bgcolor,
      color,
      '& .MuiChip-label': { px: 0.6 },
    }}
  />
);

const AttendanceGridCompactView: React.FC<AttendanceGridCompactViewProps> = ({
  rows,
  highlightedRowId,
  rowRefs,
  canManage,
  onRowClick,
  onAssignCoverage,
  onRemoveCoverage,
  onNotRequired,
  onAddNote,
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focusIndex, setFocusIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const employeeGroups = useMemo(() => groupDashboardRowsByEmployee(rows), [rows]);

  const selectedGroups = useMemo(
    () => employeeGroups.filter((group) => selected.has(group.id)),
    [employeeGroups, selected],
  );
  const allSelected = employeeGroups.length > 0 && selected.size === employeeGroups.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleExpand = (id: string, defaultOpen: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => toggleEmployeeGroupExpanded(prev, id, defaultOpen));
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(employeeGroups.map((group) => group.id)));
  };

  const scrollToFocused = useCallback((index: number) => {
    const group = employeeGroups[index];
    if (group) rowRefs.current[group.id]?.scrollIntoView({ block: 'nearest' });
  }, [employeeGroups, rowRefs]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!containerRef.current?.contains(document.activeElement) && document.activeElement?.tagName !== 'BODY') return;
      if (employeeGroups.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIndex((i) => {
          const next = Math.min(employeeGroups.length - 1, i + 1);
          scrollToFocused(next);
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIndex((i) => {
          const next = Math.max(0, i - 1);
          scrollToFocused(next);
          return next;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const group = employeeGroups[focusIndex];
        if (group) onRowClick(group.primaryRow);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [employeeGroups, focusIndex, onRowClick, scrollToFocused]);

  const renderAssignmentTimeline = (group: EmployeeDashboardGroup) => (
    <Box sx={{ py: 0.5, px: 0.75, bgcolor: '#fafafa' }}>
      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.6rem', display: 'block', mb: 0.5 }}>
        Assignment Timeline
      </Typography>
      {group.assignments.map((assignment) => (
        <Box
          key={assignment.id}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.2fr 0.8fr 0.8fr',
            gap: 0.5,
            py: 0.35,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ fontSize: '0.62rem', fontWeight: 600 }}>
            {formatAssignmentWindow(assignment)}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.62rem' }}>
            {assignment.campaignName}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.62rem' }}>
            {assignment.attendanceStatus?.replace(/_/g, ' ')}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.62rem' }}>
            {assignment.coverageStatus?.replace(/_/g, ' ')}
            {assignment.coveredByName ? ` · ${assignment.coveredByName}` : ''}
          </Typography>
        </Box>
      ))}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 0.5, mt: 0.5 }}>
        {[
          ['Clock In', group.clockInTime || '-'],
          ['Clock Out', group.clockOutTime || '-'],
          ['Late Minutes', group.lateMinutes > 0 ? group.lateMinutes : '-'],
          ['Mode', formatCoverageModeLabel(group.coverageMode)],
        ].map(([label, value]) => (
          <Box key={String(label)}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem' }}>{label}</Typography>
            <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem' }}>{value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderDataRow = (group: EmployeeDashboardGroup, index: number) => {
    const isOpen = getEmployeeGroupExpanded(expanded, group.id, group.hasMultipleAssignments);
    const isFocused = focusIndex === index;
    const attendance = getCompactAttendanceBadge(group);
    const coverage = getCompactCoverageBadge(group.coverageStatus);
    const rowBg = highlightedRowId === group.id || highlightedRowId === group.primaryRow?.id
      ? '#fff9c4'
      : isFocused
        ? `${OPS_COLORS.coverage}10`
        : getCompactRowHighlight({
          attendanceStatus: group.attendanceStatus,
          coverageStatus: group.coverageStatus,
          requiresCoverage: group.requiresCoverage,
          isCoveringOthers: group.isCoveringOthers,
        });
    const shiftLabel = group.hasMultipleAssignments
      ? `${group.assignments.length} assignments`
      : formatAssignmentWindow(group.assignments[0]);
    const campaignLabel = group.hasMultipleAssignments
      ? 'Multiple campaigns'
      : group.assignments[0]?.campaignName || '—';

    return (
      <React.Fragment key={group.id}>
        <TableRow
          hover
          ref={(el) => { rowRefs.current[group.id] = el; }}
          selected={selected.has(group.id)}
          onClick={() => {
            setFocusIndex(index);
            onRowClick(group.primaryRow);
          }}
          sx={{
            cursor: 'pointer',
            bgcolor: rowBg,
            outline: highlightedRowId === group.id ? '2px solid #fbc02d' : undefined,
            height: COMPACT_ROW_HEIGHT,
          }}
        >
          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={{ ...cellSx, ...stickySx(0), bgcolor: rowBg, width: 28 }}>
            <Checkbox size="small" checked={selected.has(group.id)} onChange={() => toggleSelect(group.id)} sx={{ p: 0 }} />
          </TableCell>
          <TableCell onClick={(e) => toggleExpand(group.id, group.hasMultipleAssignments, e)} sx={{ ...cellSx, ...stickySx(28), bgcolor: rowBg, width: 22, px: 0.25 }}>
            <IconButton size="small" sx={{ p: 0 }}>
              {isOpen ? <CollapseIcon sx={{ fontSize: 12 }} /> : <ExpandIcon sx={{ fontSize: 12 }} />}
            </IconButton>
          </TableCell>
          <TableCell sx={{ ...cellSx, ...stickySx(50), bgcolor: rowBg, fontWeight: 600, maxWidth: 120 }}>
            <Typography variant="inherit" noWrap title={group.agentName}>{group.agentName}</Typography>
          </TableCell>
          <TableCell sx={{ ...cellSx, ...stickySx(170), bgcolor: rowBg, minWidth: 118, maxWidth: 130 }}>
            <Typography variant="inherit" noWrap title={campaignLabel} sx={{ fontWeight: 600, maxWidth: 110 }}>
              {campaignLabel}
            </Typography>
          </TableCell>
          <TableCell sx={cellSx}>
            <Typography variant="inherit" sx={{ fontSize: '0.65rem' }}>{shiftLabel}</Typography>
          </TableCell>
          <TableCell sx={cellSx}>
            <StatusChip label={attendance.label} bgcolor={attendance.bgcolor} color={attendance.color} />
          </TableCell>
          <TableCell sx={cellSx}>
            <StatusChip label={coverage.label} bgcolor={coverage.bgcolor} color={coverage.color} />
          </TableCell>
          <TableCell onClick={(e) => e.stopPropagation()} sx={{ ...cellSx, width: 72, px: 0.25 }}>
            <Stack direction="row" spacing={0} justifyContent="flex-end">
              <Tooltip title="Details" arrow>
                <IconButton size="small" sx={{ p: 0.2 }} onClick={() => onRowClick(group.primaryRow)}>
                  <DetailsIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              {canManage && (
                <Tooltip title="Coverage" arrow>
                  <IconButton size="small" sx={{ p: 0.2 }} onClick={() => onAssignCoverage(group.primaryRow)}>
                    <CoverageIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              {canManage && group.coverageAssignmentId && onRemoveCoverage && (
                <Tooltip title="Remove Coverage" arrow>
                  <IconButton size="small" sx={{ p: 0.2, color: 'error.main' }} onClick={() => onRemoveCoverage(group.primaryRow)}>
                    <RemoveCoverageIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              {canManage && (
                <Tooltip title="Notes" arrow>
                  <IconButton size="small" sx={{ p: 0.2 }} onClick={() => onAddNote(group.primaryRow)}>
                    <NotesIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={8} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              {renderAssignmentTimeline(group)}
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <Box ref={containerRef} tabIndex={0} sx={{ outline: 'none', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mb: 0.25, px: 0.25 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
          {employeeGroups.length} employees · {rows.length} assignments
        </Typography>
      </Stack>

      {selected.size > 0 && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 0.75, py: 0.25, mb: 0.25, bgcolor: `${OPS_COLORS.coverage}12`, borderRadius: 0.5 }}>
          <Typography variant="caption" fontWeight={700}>{selected.size} selected</Typography>
          {canManage && (
            <>
              <Button size="small" variant="text" sx={{ minWidth: 0, fontSize: '0.62rem', py: 0, textTransform: 'none' }} onClick={() => {
                const target = selectedGroups
                  .map((group) => group.assignments.find((row) => row.coverageStatus === 'NOT_COVERED'))
                  .find(Boolean);
                if (target) onAssignCoverage(target);
              }}
              >
                Assign Coverage
              </Button>
              {selectedGroups.some((group) => canShowMarkCoverageNotRequired(group.primaryRow)) && (
              <Button size="small" variant="text" sx={{ minWidth: 0, fontSize: '0.62rem', py: 0, textTransform: 'none' }} onClick={() => {
                const target = selectedGroups
                  .map((group) => group.primaryRow)
                  .find((row) => canShowMarkCoverageNotRequired(row));
                if (target) onNotRequired(target);
              }}
              >
                Not Required
              </Button>
              )}
            </>
          )}
          <Button size="small" variant="text" sx={{ minWidth: 0, fontSize: '0.62rem', py: 0, textTransform: 'none' }} onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </Stack>
      )}

      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          flex: 1,
          maxHeight: 'calc(100vh - 248px)',
          minHeight: 420,
          overflow: 'auto',
        }}
      >
        <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': cellSx }}>
          <TableHead>
            <TableRow sx={{ height: COMPACT_HEADER_HEIGHT }}>
              <TableCell padding="checkbox" sx={{ ...cellSx, ...stickySx(0, true), width: 28, height: COMPACT_HEADER_HEIGHT, bgcolor: '#f4f6f8', zIndex: 5 }}>
                <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={toggleSelectAll} sx={{ p: 0 }} />
              </TableCell>
              <TableCell sx={{ ...cellSx, ...stickySx(28, true), width: 22, height: COMPACT_HEADER_HEIGHT, bgcolor: '#f4f6f8', zIndex: 5 }} />
              <TableCell sx={{ ...cellSx, ...stickySx(50, true), minWidth: 120, height: COMPACT_HEADER_HEIGHT, fontWeight: 700, bgcolor: '#f4f6f8', zIndex: 5 }}>
                Employee
              </TableCell>
              <TableCell sx={{ ...cellSx, ...stickySx(170, true), minWidth: 118, height: COMPACT_HEADER_HEIGHT, fontWeight: 700, bgcolor: '#f4f6f8', zIndex: 5 }}>
                Campaign
              </TableCell>
              {['Shift', 'Attendance', 'Coverage', 'Actions'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#f4f6f8', height: COMPACT_HEADER_HEIGHT, fontSize: '0.65rem' }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {employeeGroups.map((group, index) => renderDataRow(group, index))}
            {employeeGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 2 }}>No records for selected filters.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AttendanceGridCompactView;
